import React, { useEffect, useState } from "react";
import { Button, Layout, Menu, Dropdown, Modal } from "antd";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AiOutlineMenu,
  AiOutlineClose,
  AiOutlineMenuFold,
} from "react-icons/ai";
import {
  RiDashboardFill,
  RiLockPasswordLine,
  RiCalendarScheduleLine,
} from "react-icons/ri";
import { SiCoursera } from "react-icons/si";
import { MdPersonOutline, MdAssignment, MdAssessment } from "react-icons/md";
import { FaRegCircleUser } from "react-icons/fa6";
import { IoPerson } from "react-icons/io5";
import { BsSun, BsMoon } from "react-icons/bs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import logo from "../asset/logowhite.png";
import SummaryApi from "../common";
import scrollTop from "../helpers/scrollTop";

const { Header, Content } = Layout;

const moreItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "16px",
  textDecoration: "none",
  padding: "10px 0",
  borderBottom: "1px solid #f0f0f0",
};

const InstructorPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state?.user?.user);
  
  // CHANGE 1: Set initial state to false so it starts closed/collapsed
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  
  const [instructorName, setInstructorName] = useState("");
  const [isActive, setIsActive] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const [isMoreVisible, setIsMoreVisible] = useState(false);
  const [isSessionValid, setIsSessionValid] = useState(null);
  const [selectedKey, setSelectedKey] = useState("dashboard");
  const [isDark, setIsDark] = useState(false);

  const instructorId = sessionStorage.getItem("instructor_id");
  const loginIdentifier = sessionStorage.getItem("login_identifier");
  const sessionToken = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");

  // Theme handling
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const shouldBeDark = savedTheme === "dark";
    setIsDark(shouldBeDark);
    
    if (shouldBeDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  useEffect(() => {
    const handleThemeChange = () => {
      const isCurrentlyDark = document.documentElement.classList.contains("dark");
      setIsDark(isCurrentlyDark);
    };

    window.addEventListener("themeChanged", handleThemeChange);
    
    const handleStorageChange = (e) => {
      if (e.key === "theme") {
        handleThemeChange();
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    
    if (newIsDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
    
    window.dispatchEvent(new Event("themeChanged"));
  };

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("dashboard")) setSelectedKey("dashboard");
    else if (path.includes("instructor-courses")) setSelectedKey("instructor-courses");
    else if (path.includes("addCourseSchedule")) setSelectedKey("addCourseSchedule");
    else if (path.includes("instructorCourseSchedule")) setSelectedKey("instructorCourseSchedule");
    else if (path.includes("instructor-profile")) setSelectedKey("instructor-profile");
    else if (path.includes("assignments-instructor")) setSelectedKey("assignments-instructor");
    else if (path.includes("curriculum") || path.includes("course-curriculum")) {
      if (!selectedKey || selectedKey === "dashboard") setSelectedKey("instructor-courses");
    }
  }, [location.pathname, selectedKey]);

const handleLogout = () => {
  Modal.confirm({
    title: "Are you sure you want to logout?",
    content: "You will need to log in again to access your instructor panel.",
    okText: "Logout",
    okType: "danger",
    cancelText: "Cancel",
    onOk() {
      // Clear all storage first
      sessionStorage.clear();
      localStorage.clear();
      
      // Show logout message
      toast.info("Logged out successfully");
      
      // Add a flag to indicate this is a manual logout (not session expiry)
      sessionStorage.setItem("logout_flag", "true");
      
      // Navigate to login page
      navigate("/instructor-login", { replace: true });
    },
  });
};
  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleMenuClick = (key) => {
    setSelectedKey(key);
    scrollTop();
  };

  // CHANGE 2: Removed logic that forced sidebar open on desktop resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 576;
      setIsMobile(mobile);
      // Only force close if moving to mobile, otherwise keep user preference
      if (mobile) {
        setIsSidebarVisible(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const verifySession = async () => {
      if (!instructorId || !loginIdentifier || !sessionToken) {
        setIsSessionValid(false);
        setIsActive(false);
        toast.error("Session data missing. Please log in again.");
        navigate("/instructor-login", { replace: true });
        return;
      }

      try {
        const response = await fetch(SummaryApi.instructorLoginStatus.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ login_identifier: loginIdentifier }),
        });

        const data = await response.json();
        if (data.status && data.data?.is_active === 1 && data.data?.is_otp_verification === 1) {
          setIsSessionValid(true);
          setIsActive(true);
          // CHANGE 3: Removed setIsSidebarVisible(!isMobile) here so it doesn't auto-open
        } else if (window.location.pathname.includes("edit-instructor")) {
          setIsSessionValid(true);
          setIsActive(false);
          setIsSidebarVisible(false);
        } else {
          setIsSessionValid(false);
          setIsActive(false);
          sessionStorage.clear();
          toast.error(data.message || "Session expired or invalid. Please log in again.");
          navigate("/instructor-login", { replace: true });
        }
      } catch (error) {
        setIsSessionValid(false);
        setIsActive(false);
        sessionStorage.clear();
        toast.error("Error verifying session. Please log in again.");
        navigate("/instructor-login", { replace: true });
      }
    };

    verifySession();
  }, [instructorId, loginIdentifier, sessionToken, navigate, isMobile]);

  useEffect(() => {
    if (instructorId && isSessionValid) {
      fetch(SummaryApi.instructorDetailsByInstructorId.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instructor_id: Number(instructorId) }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status && data.data?.instructor_name) {
            setInstructorName(data.data.instructor_name);
            setIsActive(data.data.is_active === 1);
          }
        })
        .catch((err) => console.error("Error fetching instructor name:", err));
    }
  }, [instructorId, isSessionValid]);

  const menuItems = [
    { key: "dashboard", icon: <RiDashboardFill />, label: "Dashboard" },
    { key: "instructor-courses", icon: <SiCoursera />, label: "Courses" },
    { key: "addCourseSchedule", icon: <RiCalendarScheduleLine />, label: "Add Course Schedule" },
    { key: "instructorCourseSchedule", icon: <RiCalendarScheduleLine />, label: "Course Schedule" },
    { key: "instructor-profile", icon: <MdPersonOutline />, label: "Profile" },
  ];

const dropdownMenu = (
  <Menu 
    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
    items={[
      {
        key: '1',
        label: (
          <div 
            onClick={() => navigate("/instructor-panel/instructor-profile")}
            className="text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white flex items-center gap-2 px-4 py-1"
          >
            <FaRegCircleUser className="text-lg" />
            Profile
          </div>
        ),
      },
      {
        key: '2',
        label: (
          <div 
            onClick={handleLogout}
            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center gap-1 px-4 py-1"
          >
            <RiLockPasswordLine className="text-lg" />
            Logout
          </div>
        ),
      },
    ]}
  />
);

  const hideSidebarAndDropdown = location.pathname.includes("edit-instructor") || location.pathname.includes("instructor-access-denied");

  if (isSessionValid === null || isActive === null) {
    return (
      <div className="min-h-screen w-full bg-white dark:bg-gray-900 flex items-center justify-center px-4 py-6 transition-colors duration-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-cyan-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!isSessionValid) {
    return null;
  }

  return (
    <Layout style={{ minHeight: "100vh" }} className="bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Header */}
      <Header
        className="bg-[#001529] dark:bg-gray-800 transition-colors duration-300"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          padding: "0 24px",
          height: "64px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {/* Left Side: Menu Toggle and Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {!isMobile && !hideSidebarAndDropdown && (
            <Button
              type="text"
              onClick={toggleSidebar}
              className="text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
              style={{
                fontSize: "20px",
                color: "white",
                background: "transparent",
                border: "none",
                padding: 0,
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isSidebarVisible ? <AiOutlineClose /> : <AiOutlineMenu />}
            </Button>
          )}

          <div className="header-title">
            <a href="/instructor-panel/dashboard">
   <img src={logo} alt="logo" className="w-[120px]" />
            </a>
         
          </div>
        </div>

        {/* Right Side: Theme Toggle and User Dropdown */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {!location.pathname.includes("edit-instructor") && 
          !location.pathname.includes("instructor-access-denied") && (
            <Button
              type="text"
              onClick={toggleTheme}
              className="text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
              style={{
                fontSize: "18px",
                color: "white",
                background: "transparent",
                border: "none",
                padding: 0,
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isDark ? (
                <BsSun style={{ color: "#fbbf24" }} />
              ) : (
                <BsMoon style={{ color: "#ffffff" }} />
              )}
            </Button>
          )}

          {!hideSidebarAndDropdown && isActive && (
            <Dropdown overlay={dropdownMenu} placement="bottomRight" trigger={['click']}>
              <Button 
                className="text-white bg-transparent hover:bg-white hover:bg-opacity-10 border border-white border-opacity-30 transition-colors duration-200"
                style={{ 
                  color: "white", 
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  height: "40px",
                }}
              >
                <IoPerson style={{ fontSize: "16px" }} />
                <span style={{ margin: 0, fontSize: "14px" }}>
                  {instructorName || "Loading..."}
                </span>
              </Button>
            </Dropdown>
          )}
        </div>
      </Header>

      {/* Collapsed Sidebar Icon - This shows when sidebar is closed on desktop */}
      {!isMobile && !isSidebarVisible && !hideSidebarAndDropdown && (
        <div
          className="bg-[#001529] dark:bg-gray-800 transition-colors duration-300"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "60px",
            height: "100vh",
            color: "white",
            zIndex: 999,
            overflow: "hidden",
            transition: "width 0.3s ease",
            borderRight: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <div
            className="header-title bg-[#001529] dark:bg-gray-800 transition-colors duration-300"
            style={{
              color: "white",
              textAlign: "center",
              padding: "1.2rem 0",
              background: "rgba(0, 0, 0, 0.15)",
            }}
          >
            <Button
              type="text"
              onClick={toggleSidebar}
              className="text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
              style={{
                fontSize: "20px",
                color: "white",
                background: "transparent",
                border: "none",
                padding: 0,
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
              }}
            >
              <AiOutlineMenu />
            </Button>
          </div>

          <div style={{ height: "calc(100vh - 80px)", overflowY: "auto" }}>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[selectedKey]}
              className="bg-[#001529] dark:bg-gray-800 transition-colors duration-300"
              style={{ border: "none", background: "transparent" }}
              items={menuItems.map((item) => ({
                key: item.key,
                icon: React.cloneElement(item.icon, { 
                  className: `${
                    selectedKey === item.key 
                      ? "text-blue-400 dark:text-cyan-400" 
                      : "text-white dark:text-gray-300"
                  }` 
                }),
                label: (
                  <Link
                    to={item.key}
                    onClick={() => handleMenuClick(item.key)}
                    className={`${
                      selectedKey === item.key 
                        ? "text-blue-400 dark:text-cyan-400" 
                        : "text-white dark:text-gray-300 hover:text-blue-200 dark:hover:text-cyan-300"
                    } transition-colors duration-200`}
                    style={{ 
                      display: "block", 
                      textAlign: "center",
                      padding: "12px 0",
                    }}
                    title={item.label}
                  >
                  </Link>
                ),
                className: `${
                  selectedKey === item.key 
                    ? "bg-white bg-opacity-20" 
                    : "hover:bg-white hover:bg-opacity-10"
                } transition-colors duration-200`
              }))}
            />
          </div>
        </div>
      )}

      {/* Desktop Sidebar (Expanded) */}
      {!isMobile && !hideSidebarAndDropdown && isSidebarVisible && (
        <div
          className="bg-[#001529] dark:bg-gray-800 transition-colors duration-300"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "250px",
            height: "100vh",
            color: "white",
            zIndex: 999,
            overflow: "auto",
            transition: "transform 0.3s ease",
            borderRight: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <div
            className="header-title bg-[#001529] dark:bg-gray-800 transition-colors duration-300"
            style={{
              color: "white",
              textAlign: "center",
              padding: "1.2rem 0",
              background: "rgba(0, 0, 0, 0.15)",
              position: "relative",
            }}
          >
            <img
              src={logo}
              alt="logo"
              className="w-[60%] mx-auto"
              style={{
                objectFit: "contain",
                filter: "drop-shadow(0 0 6px rgba(255, 164, 4, 0.6))",
              }}
            />
          </div>

          <div style={{ height: "calc(100vh - 200px)", overflowY: "auto" }}>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[selectedKey]}
              className="bg-[#001529] dark:bg-gray-800 transition-colors duration-300"
              style={{ border: "none", background: "transparent" }}
              items={menuItems.map((item) => ({
                key: item.key,
                icon: React.cloneElement(item.icon, { 
                  className: `${
                    selectedKey === item.key 
                      ? "text-blue-400 dark:text-cyan-400" 
                      : "text-white dark:text-gray-300"
                  }` 
                }),
                label: (
                  <Link
                    to={item.key}
                    onClick={() => handleMenuClick(item.key)}
                    className={`${
                      selectedKey === item.key 
                        ? "text-blue-400 dark:text-cyan-400" 
                        : "text-white dark:text-gray-300 hover:text-blue-200 dark:hover:text-cyan-300"
                    } transition-colors duration-200`}
                    style={{ 
                      display: "block", 
                      textAlign: "left",
                      padding: "8px 0"
                    }}
                  >
                    {item.label}
                  </Link>
                ),
                className: `${
                  selectedKey === item.key 
                    ? "bg-white bg-opacity-20" 
                    : "hover:bg-white hover:bg-opacity-10"
                } transition-colors duration-200`
              }))}
            />
          </div>
        </div>
      )}

      {/* Mobile Sidebar */}
 

      {/* Main Content */}
      <Content
        className="bg-gray-50 dark:bg-gray-900 transition-colors duration-300"
        style={{
          marginLeft: isSidebarVisible && !isMobile && !hideSidebarAndDropdown ? 250 : (!isMobile && !isSidebarVisible && !hideSidebarAndDropdown ? 60 : 0),
          padding: isMobile ? "8px" : "24px",
          paddingBottom: isMobile ? "80px" : "24px",
          transition: "margin-left 0.3s, padding 0.3s",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Outlet />
      </Content>

      {/* Bottom Navigation for Mobile */}
      {isMobile && !hideSidebarAndDropdown && (
        <div
          className="bg-[#001529] dark:bg-gray-800 border-t border-gray-700 transition-colors duration-300"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "60px",
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          {menuItems.slice(0, 3).map((item) => (
            <Link
              key={item.key}
              to={`/instructor-panel/${item.key}`}
              onClick={() => {
                setSelectedKey(item.key);
                scrollTop();
              }}
              className={`transition-colors duration-200 ${
                selectedKey === item.key
                  ? "text-blue-400"
                  : "text-white hover:text-blue-300"
              }`}
              style={{
                textAlign: "center",
                textDecoration: "none",
                padding: "8px",
              }}
            >
              {React.cloneElement(item.icon, {
                className: `text-lg ${
                  selectedKey === item.key
                    ? "text-blue-400"
                    : "text-white"
                }`
              })}
              <div style={{ fontSize: "10px", marginTop: "2px", color: selectedKey === item.key ? "#60a5fa" : "white" }}>
                {item.label}
              </div>
            </Link>
          ))}
          <div
            onClick={() => setIsMoreVisible(true)}
            className={`cursor-pointer transition-colors duration-200 ${
              isMoreVisible 
                ? "text-blue-400 dark:text-cyan-400" 
                : "text-white dark:text-gray-300 hover:text-blue-200 dark:hover:text-cyan-300"
            }`}
            style={{
              textAlign: "center",
              padding: "8px",
            }}
          >
            <AiOutlineMenuFold style={{ fontSize: "20px" }} />
            <div style={{ fontSize: "10px", marginTop: "2px" }}>More</div>
          </div>
        </div>
      )}

      {/* Custom More Menu for Mobile */}
      {isMobile && isMoreVisible && !hideSidebarAndDropdown && (
        <div
          className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300"
          style={{
            position: "fixed",
            bottom: "60px",
            left: 0,
            width: "100%",
            background: isDark ? "#1f2937" : "#fff",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
            zIndex: 2000,
            padding: "16px 0",
          }}
        >
          <div style={{ textAlign: "right", marginBottom: "8px" , marginLeft: "20px", marginRight: "20px"}}>
            <button
              onClick={() => setIsMoreVisible(false)}
              className="text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
              style={{
                border: "none",
                background: "transparent",
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              ✕
            </button>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "0 20px",
            }}
          >
            <Link
              to="/instructor-panel/instructor-profile"
              onClick={() => {
                setIsMoreVisible(false);
                setSelectedKey("instructor-profile");
              }}
              className={`transition-colors duration-200 ${
                selectedKey === "instructor-profile" 
                  ? "text-blue-600 dark:text-cyan-400" 
                  : "text-gray-700 dark:text-white hover:text-blue-600 dark:hover:text-cyan-400"
              }`}
              style={{
                ...moreItemStyle,
                borderBottomColor: "#e5e7eb",
              }}
            >
              <MdPersonOutline /> Profile
            </Link>
            {/* <Link
              to="/instructor-panel/assignments-instructor"
              onClick={() => {
                setIsMoreVisible(false);
                setSelectedKey("assignments-instructor");
              }}
              className={`transition-colors duration-200 ${
                selectedKey === "assignments-instructor" 
                  ? "text-blue-600 dark:text-cyan-400" 
                  : "text-gray-700 dark:text-white hover:text-blue-600 dark:hover:text-cyan-400"
              }`}
              style={{
                ...moreItemStyle,
                borderBottomColor: "#e5e7eb",
              }}
            >
              <MdAssignment /> Assignments
            </Link> */}
            
            <div
              onClick={handleLogout}
              className="text-red-600 dark:text-white hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200 cursor-pointer"
              style={{ 
                ...moreItemStyle, 
                borderBottomColor: "#e5e7eb",
              }}
            >
              <RiLockPasswordLine /> Logout
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-center" autoClose={3000} />
    </Layout>
  );
};

export default InstructorPanel;